import React, { useState } from 'react';
import { ExternalLink, Heart, Volume2, Share2, Check, Music2 } from 'lucide-react';
import { YoutubeIcon } from './icons/YoutubeIcon';
import { soundEngine } from '../utils/audio';

const ACTIVE_VIDEO = {
  youtubeId: '8kGQ1LSfH6o',
  title: 'Petty - Freya Skye, tribute by Melodalis',
  category: 'Tribute Cover',
  description: 'The energetic and heartfelt vocal tribute by Melodalis celebrating Freya Skye’s hit song "Petty". Packed with passion, high energy vocals, and musical joy!',
  channelUrl: 'https://www.youtube.com/@Melodalis-music',
};

export const VideosSection: React.FC = () => {
  const [copiedLink, setCopiedLink] = useState(false);

  const handleShare = () => {
    soundEngine.playPopSfx();
    const url = `https://www.youtube.com/watch?v=${ACTIVE_VIDEO.youtubeId}`;
    navigator.clipboard.writeText(url);
    setCopiedLink(true);
    setTimeout(() => setCopiedLink(false), 2500);
  };

  return (
    <section id="videos" className="py-16 md:py-24 bg-gradient-to-b from-white via-pink-50/60 to-purple-50/40 relative">
      
      {/* Background cute accent shapes */}
      <div className="absolute top-10 right-10 text-pink-200 text-4xl select-none pointer-events-none animate-float">🎬</div>
      <div className="absolute bottom-12 left-8 text-purple-200 text-4xl select-none pointer-events-none animate-float-delay">🎤</div>

      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        
        {/* Section Header */}
        <div className="text-center max-w-3xl mx-auto mb-12 space-y-3">
          <div className="inline-flex items-center gap-2 px-4 py-1.5 rounded-full bg-red-100 text-red-600 font-bold text-xs">
            <YoutubeIcon className="w-4 h-4 fill-current" />
            <span>Official Video Stage</span>
          </div>
          <h2 className="text-3xl sm:text-4xl lg:text-5xl font-bold text-gray-800">
            Watch <span className="bg-gradient-to-r from-red-500 via-pink-500 to-purple-600 bg-clip-text text-transparent">Melodalis</span> Sing
          </h2>
          <p className="text-gray-600 text-base sm:text-lg">
            Enjoy Melodalis’ latest performance straight from the YouTube stage!
          </p>
        </div>

        {/* Featured Theater Player with Kawaii Border Frame */}
        <div className="max-w-5xl mx-auto">
          <div className="relative rounded-3xl p-3 sm:p-5 bg-gradient-to-r from-pink-300 via-purple-300 to-rose-300 shadow-2xl border-4 border-white">
            
            {/* Top Kawaii TV Header Bar */}
            <div className="flex items-center justify-between pb-3 px-2 text-white">
              <div className="flex items-center gap-2">
                <span className="w-3.5 h-3.5 rounded-full bg-red-400 border border-white" />
                <span className="w-3.5 h-3.5 rounded-full bg-yellow-300 border border-white" />
                <span className="w-3.5 h-3.5 rounded-full bg-green-400 border border-white" />
                <span className="ml-2 text-xs font-bold tracking-wide uppercase text-white/90 drop-shadow-xs flex items-center gap-1.5">
                  <Music2 className="w-3.5 h-3.5" />
                  Melodalis Stage TV
                </span>
              </div>
              <div className="flex items-center gap-2 text-xs font-bold bg-white/25 px-3 py-1 rounded-full">
                <span className="w-2 h-2 rounded-full bg-red-500 animate-pulse" />
                <span>Now Playing: {ACTIVE_VIDEO.title.split('-')[0]}</span>
              </div>
            </div>

            {/* Video Iframe Container (16:9 Aspect Ratio) */}
            <div className="relative w-full rounded-2xl overflow-hidden bg-black shadow-inner aspect-video">
              <iframe
                className="w-full h-full"
                src={`https://www.youtube.com/embed/${ACTIVE_VIDEO.youtubeId}?autoplay=0&rel=0&modestbranding=1`}
                title={ACTIVE_VIDEO.title}
                allow="accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture; web-share"
                referrerPolicy="strict-origin-when-cross-origin"
                allowFullScreen
              />
            </div>

            {/* Bottom Controls / Info Bar inside frame */}
            <div className="pt-4 px-2 flex flex-col sm:flex-row sm:items-center justify-between gap-4 text-white">
              <div>
                <h3 className="text-lg sm:text-xl font-bold drop-shadow-xs">{ACTIVE_VIDEO.title}</h3>
                <p className="text-white/85 text-xs sm:text-sm mt-0.5">{ACTIVE_VIDEO.description}</p>
              </div>
              
              <div className="flex items-center gap-2.5 shrink-0">
                <button
                  onClick={handleShare}
                  className="px-4 py-2 rounded-xl bg-white/20 hover:bg-white/30 backdrop-blur-xs font-bold text-xs flex items-center gap-1.5 transition-all cursor-pointer"
                  title="Share Video Link"
                >
                  {copiedLink ? <Check className="w-4 h-4 text-green-300" /> : <Share2 className="w-4 h-4" />}
                  <span>{copiedLink ? 'Link Copied!' : 'Share'}</span>
                </button>

                <a
                  href={`https://www.youtube.com/watch?v=${ACTIVE_VIDEO.youtubeId}`}
                  target="_blank"
                  rel="noopener noreferrer"
                  className="px-4 py-2 rounded-xl bg-red-600 hover:bg-red-700 text-white font-bold text-xs flex items-center gap-1.5 shadow-md transition-all"
                >
                  <YoutubeIcon className="w-4 h-4 fill-current" />
                  <span>Watch on YouTube</span>
                  <ExternalLink className="w-3 h-3 ml-0.5" />
                </a>
              </div>
            </div>

          </div>
        </div>

        {/* Channel Subscriber Call to Action Banner */}
        <div className="mt-12 max-w-4xl mx-auto rounded-3xl p-6 sm:p-8 bg-gradient-to-r from-pink-500 via-rose-500 to-purple-600 text-white shadow-xl flex flex-col sm:flex-row items-center justify-between gap-6">
          <div className="space-y-1 text-center sm:text-left">
            <div className="inline-flex items-center gap-1.5 px-3 py-1 rounded-full bg-white/20 text-xs font-bold">
              <Heart className="w-3.5 h-3.5 fill-white" />
              <span>Join Melodalis on YouTube</span>
            </div>
            <h3 className="text-2xl sm:text-3xl font-bold">Never Miss a Single Song!</h3>
            <p className="text-pink-100 text-sm max-w-md">
              Support Melodalis’ journey by subscribing to the official YouTube channel for upcoming covers and music releases.
            </p>
          </div>

          <a
            href="https://www.youtube.com/@Melodalis-music?sub_confirmation=1"
            target="_blank"
            rel="noopener noreferrer"
            onClick={() => soundEngine.playSparkleSfx()}
            className="px-6 py-3.5 rounded-2xl bg-white text-red-600 hover:bg-red-50 font-bold text-sm shadow-md hover:scale-105 active:scale-95 transition-all flex items-center gap-2 shrink-0 cursor-pointer"
          >
            <YoutubeIcon className="w-5 h-5 fill-current" />
            <span>Subscribe on YouTube</span>
          </a>
        </div>

      </div>
    </section>
  );
};
